// /projects/<id>/files - file-management page for one project.
//
// Bug-#8 follow-up: NewProjectRoute used to render a full inline
// list of every file already attached to a project, which became
// unworkable once Avonmouth-shape projects landed (28+ files). The
// inline list is now a compact "N files attached · Manage" badge
// that navigates here. The operator gets a dedicated screen for
// browsing, opening, and deleting files - actions that didn't have
// a home before.
//
// Add files: a 3-tab segmented control between Local files / Google Drive / Gmail.
// All three sources land in the same `pickedFiles` queue and are uploaded through
// the upload provider; each file is then read in the background.
import { useContext, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { UserContext } from "../auth";

import {
  kindLabel,
  useProject,
  useExcludeWrongSite,
  useSetUploadFreshness,
  useUploadDelete,
  useUploadDownloadUrl,
  useUploadRefresh,
  useUploadsList,
  useDeletedArrivals,
  SightingsByUploadProvider,
  UploadsExtractedProvider,
} from "../api";
import type { DeletedArrival, UploadRecord } from "../api";
import { SightingList, DeprecatedBadge, threadMessageCount } from "../components/SightingList";
import { projectDisplayName } from "../utils/projectName";
import { useUploadsTree } from "../api/hooks/useDriveSync";
import { plural, summariseTree } from "./projectFilesCounts";
import { isDemoMode } from "../demo/demoMode";
import { DemoBlur } from "../components/DemoBlur";
import { DrivePickerPanel } from "../components/DrivePickerPanel";
import { DuplicateResolverModal } from "../components/DuplicateResolverModal";
import { DriveSyncedFilesTree } from "../components/DriveSyncedFilesTree";
import { FailedExtractionsPanel } from "../components/FailedExtractionsPanel";
import { GmailSyncedFilesTree } from "../components/GmailSyncedFilesTree";
import { GmailSearchPanel } from "../components/GmailSearchPanel";
import { ExtractedContentPanel } from "../components/ExtractedContentPanel";
import { ExtractionIssueMarker } from "../components/ExtractionIssueMarker";
import { ExtractionStatusButton } from "../components/ExtractionStatusButton";
import { ExtractionProgressBar } from "../components/ExtractionProgressBar";
import { ExtractionSummaryBar } from "../components/ExtractionSummaryBar";
import {
  EmailThreadAttachmentsToggle,
  EmailThreadAttachmentsRows,
  EmailThreadTypeCell,
} from "../components/EmailThreadAttachments";
import { OutOfDateIcon } from "../components/OutOfDateIcon";
import { PossibleOtherSiteBadge, SiteWarningBadge } from "../components/SiteWarningBadge";
import { ExtractedClausesSection } from "../components/ExtractedClausesSection";
import { RegisterBadge, RegistersForDocumentsProvider } from "../components/RegisterBadge";
import { ReextractLink } from "../components/ReextractLink";
import {
  useLatestUploadForProject,
  useUploadSubmitContext,
} from "../state/useUploadSubmitContext";
import { useProjectFileIntake } from "../state/useProjectFileIntake";
import styles from "./ProjectFilesRoute.module.css";
import { Chevron } from "../components/Chevron";

type UploadSource = "local" | "drive" | "gmail";

const formatBytes = (n: number): string => {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

const formatTimestamp = (iso: string): string => {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

// Provenance line for a row that has NO sighting to render. Only three
// populations reach this: BMA-synced production_site rows (12 in prod), and the
// calculated / agent-generated artefacts the platform writes itself - none of
// which go through an ingest door that records a sighting. Keyed on the canonical
// ingestion origin (`metadata_blob.source`, principle 17), NOT on a sighting's
// unreliable `source`, and worded to match the equivalent SightingList line.
const provenanceFallback = (u: UploadRecord): string => {
  switch (u.source) {
    case "production_site":
      return "synced from the Production site";
    case "calculated":
      return "generated from a cross-site procurement comparison";
    case "agent_generated":
      return "generated by an agent";
    case "drive":
      return "picked from Google Drive";
    case "gmail":
      return u.pulled_from ? `imported from Gmail (${u.pulled_from})` : "imported from Gmail";
    default:
      return "uploaded";
  }
};

// Provenance detail that lives on the UPLOAD, not on any sighting, so
// SightingList structurally cannot render it: the mailbox a hand-imported Gmail
// thread came from, and when a Drive/Gmail-picked row was last re-fetched from
// its source. Both were only in the retired Source badge's hover tooltip.
const provenanceDetail = (u: UploadRecord): string => {
  const parts: string[] = [];
  if (u.source === "gmail" && u.pulled_from) parts.push(`from ${u.pulled_from}`);
  if (
    (u.source === "gmail" || u.source === "drive") &&
    u.last_refreshed_at
  ) {
    parts.push(`refreshed ${formatTimestamp(u.last_refreshed_at)}`);
  }
  return parts.join(" · ");
};

// The count beside a section heading. Lighter and smaller than the heading it
// sits in, so the heading still reads as the heading and the number as a stat -
// and inside the <h2>, so it is announced with it rather than as a stray figure.
const SectionCount = ({ children }: { children: React.ReactNode }) => (
  <span
    style={{
      marginLeft: 10,
      fontSize: 13,
      fontWeight: 400,
      color: "#6b7280",
      whiteSpace: "nowrap",
    }}
  >
    {children}
  </span>
);

export const ProjectFilesRoute = () => {
  // React Router v6 already URL-decodes ``useParams`` values, so a
  // second decodeURIComponent here would double-decode and silently
  // corrupt any project ID containing a literal '%' (encoded as '%25').
  const { projectId } = useParams<{ projectId: string }>();

  const { data: fileProject } = useProject(projectId);
  const uploadsQuery = useUploadsList(projectId);
  // Feeds the Drive and email section headers, which count what those two TREES
  // render - a different payload from ``useUploadsList`` above, which filters
  // rows differently. Shared query key, so react-query serves it from the entry
  // the trees already populate rather than issuing a second request. Declared up
  // here with the other hooks: there is an early return below, and a hook after
  // it would not run on every render.
  const uploadsTreeQuery = useUploadsTree(projectId);
  // Operator-deleted files still arriving via an automated path (design 3.3
  // step 3) - shown in a collapsed section so the suppression is visible.
  const deletedArrivals = useDeletedArrivals(projectId);
  const downloadUrl = useUploadDownloadUrl();
  const deleteUploads = useUploadDelete(projectId);
  const refreshUploads = useUploadRefresh(projectId);
  const setFreshness = useSetUploadFreshness(projectId ?? "");
  const excludeWrongSite = useExcludeWrongSite(projectId ?? "");
  const uploader = useUploadSubmitContext();
  // Per-project view of the global submits stack: this page only
  // reacts to its own project's most recent submit, never to a
  // different project's in-flight pill.
  const thisProjectSubmit = useLatestUploadForProject(projectId);
  const { user } = useContext(UserContext);
  // Demo mode allows uploading + re-running, but must not expose raw documents
  // (un-anonymisable customer content) or delete real files, so file
  // open/download + delete are disabled here. The central API guard blocks the
  // backing calls too; the UI disable also covers Open-in-Drive (bypasses our
  // API) and gives a cleaner affordance than a failed click.
  const demo = isDemoMode();

  // Picked-but-not-yet-uploaded files. They live here until the operator
  // hits "Add files", which uploads them; the list below then refreshes.
  // Shared file-intake state - same picked-file queue, same-name duplicate modal, and reuse of already-attached
  // unchanged files as the new-project form. Keyed on this project's
  // existing uploads so re-picking a Drive folder reuses identical files
  // instead of re-uploading them.
  const {
    files: pickedFiles,
    addFiles,
    onPickFiles,
    removeFile,
    reset: resetFileIntake,
    pendingDeletes,
    clearPendingDeletes,
    reusedAttachedUploads,
    reuseUploadsForSubmit,
    drivePickerReuse,
    dupResolverProps,
  } = useProjectFileIntake(uploadsQuery.data);
  const [dragOver, setDragOver] = useState(false);
  const [uploadSource, setUploadSource] = useState<UploadSource>("local");
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // When this project's most recent submit settles to "done",
  // clear the local upload queue so the operator doesn't
  // accidentally re-submit the same batch on the next click.
  useEffect(() => {
    if (thisProjectSubmit?.stage === "done") {
      resetFileIntake();
    }
  }, [thisProjectSubmit?.stage, resetFileIntake]);

  // Each row's section is decided once, by the BE classifier ``/uploads/tree`` also
  // reads, so the lists below render by it and never re-derive it.
  const uploads = (uploadsQuery.data ?? []).filter((u) => u.files_section === "direct");
  const unaccounted = (uploadsQuery.data ?? []).filter(
    (u) => u.files_section === "unaccounted",
  );
  const uploadIds = [...uploads, ...unaccounted].map((u) => u.upload_id);
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  // A row that moved to another section, or went, can no longer be acted on here.
  const shownIdsKey = uploadIds.join("\n");
  useEffect(() => {
    const shown = new Set(shownIdsKey.split("\n"));
    setSelected((prev) => {
      const kept = [...prev].filter((id) => shown.has(id));
      return kept.length === prev.size ? prev : new Set(kept);
    });
  }, [shownIdsKey]);
  const [busyOpen, setBusyOpen] = useState<string | null>(null);
  const [busyDelete, setBusyDelete] = useState<string | null>(null);
  const [busyFreshness, setBusyFreshness] = useState<string | null>(null);
  const [busyQuarantine, setBusyQuarantine] = useState<string | null>(null);
  const [busyRefresh, setBusyRefresh] = useState<Set<string>>(() => new Set());
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  const toggleExpanded = (uploadId: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(uploadId)) {
        next.delete(uploadId);
      } else {
        next.add(uploadId);
      }
      return next;
    });
  };
  // Email-thread rows carry a second, INDEPENDENT expander state: the chevron
  // before the filename toggles a nested attachments tree, separate from the
  // extracted-content panel above. Two-state because they show different things
  // (attachments vs. the thread's own text) and an operator often wants one
  // without the other.
  const [attachmentsExpanded, setAttachmentsExpanded] = useState<Set<string>>(
    () => new Set(),
  );
  const toggleAttachmentsExpanded = (uploadId: string) => {
    setAttachmentsExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(uploadId)) {
        next.delete(uploadId);
      } else {
        next.add(uploadId);
      }
      return next;
    });
  };
  const onDeleteOne = async (uploadId: string, name: string | null) => {
    const label = name ?? uploadId;
    const ok = window.confirm(
      `Delete "${label}"?\n\nRemoves it from this project. Cannot be undone.`,
    );
    if (!ok) return;
    setBusyDelete(uploadId);
    try {
      await deleteUploads.mutateAsync([uploadId]);
      setSelected((s) => {
        if (!s.has(uploadId)) return s;
        const next = new Set(s);
        next.delete(uploadId);
        return next;
      });
    } catch (e) {
      window.alert(
        `Delete failed: ${e instanceof Error ? e.message : String(e)}`,
      );
    } finally {
      setBusyDelete(null);
    }
  };

  const onExcludeWrongSiteOne = async (uploadId: string, name: string | null) => {
    const label = name ?? uploadId;
    const note = window.prompt(
      `Mark "${label}" as the wrong site?\n\nThis EXCLUDES it: held back from this ` +
        `project's data + hidden from agents (reversible from the wrong-site excluded ` +
        `section on the Settings page). Optionally note which site it belongs to:`,
      "",
    );
    if (note === null) return; // cancelled
    setBusyQuarantine(uploadId);
    try {
      await excludeWrongSite.mutateAsync({
        upload_id: uploadId,
        user_email: user?.email ?? undefined,
        note: note || undefined,
      });
    } catch (e) {
      window.alert(
        `Exclude failed: ${e instanceof Error ? e.message : String(e)}`,
      );
    } finally {
      setBusyQuarantine(null);
    }
  };

  const toggle = (uploadId: string) =>
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(uploadId)) next.delete(uploadId);
      else next.add(uploadId);
      return next;
    });

  const onOpen = async (uploadId: string) => {
    setBusyOpen(uploadId);
    try {
      const resp = await downloadUrl.mutateAsync(uploadId);
      // Open in a new tab so the operator doesn't lose the file
      // list. Most browsers honour the noopener for security.
      window.open(resp.url, "_blank", "noopener,noreferrer");
    } catch (e) {
      window.alert(
        `Couldn't open file: ${e instanceof Error ? e.message : String(e)}`,
      );
    } finally {
      setBusyOpen(null);
    }
  };

  const onToggleFreshness = async (uploadId: string, outOfDate: boolean) => {
    if (!projectId) return;
    setBusyFreshness(uploadId);
    try {
      await setFreshness.mutateAsync({
        upload_id: uploadId,
        project_id: projectId,
        out_of_date: outOfDate,
      });
    } catch (e) {
      window.alert(
        `Couldn't update file freshness: ${
          e instanceof Error ? e.message : String(e)
        }`,
      );
    } finally {
      setBusyFreshness(null);
    }
  };

  const onRefreshRows = async (rows: typeof uploads) => {
    // Drive rows are always refreshable; Gmail rows only by the
    // operator who imported them (pulled_by). Filter the same way the
    // per-row Refresh button is gated so a bulk action can't sneak
    // through a row the user couldn't refresh individually.
    const refreshable = rows.filter(
      (r) =>
        (r.source === "drive" && typeof r.drive_file_id === "string") ||
        (r.source === "gmail" &&
          typeof r.gmail_thread_id === "string" &&
          r.pulled_by === user?.id),
    );
    if (refreshable.length === 0) {
      window.alert(
        "Nothing to refresh - only files imported from Google Drive, or " +
          "Gmail threads you imported yourself, can be refreshed (local " +
          "uploads have no canonical 'latest' to pull).",
      );
      return;
    }
    setBusyRefresh((s) => {
      const next = new Set(s);
      refreshable.forEach((r) => next.add(r.upload_id));
      return next;
    });
    try {
      const result = await refreshUploads.mutateAsync(refreshable);
      if (result.errors.length > 0) {
        const summary = result.errors
          .map(
            (e) =>
              `  - ${refreshable.find((r) => r.upload_id === e.upload_id)?.original_name ?? e.upload_id}: ${e.message}`,
          )
          .join("\n");
        window.alert(
          `Refreshed ${result.refreshed.length} of ${refreshable.length} file${
            refreshable.length === 1 ? "" : "s"
          }. Failures:\n${summary}`,
        );
      }
    } catch (e) {
      window.alert(
        `Refresh failed: ${e instanceof Error ? e.message : String(e)}`,
      );
    } finally {
      setBusyRefresh((s) => {
        const next = new Set(s);
        refreshable.forEach((r) => next.delete(r.upload_id));
        return next;
      });
    }
  };

  const onBulkDelete = async () => {
    const ids = [...selected];
    if (ids.length === 0) return;
    const projectLabel = fileProject ? projectDisplayName(fileProject) : projectId;
    const msg = `Delete ${ids.length} file${ids.length === 1 ? "" : "s"} from "${projectLabel}"? This deletes the underlying S3 object too and cannot be undone.`;
    if (!window.confirm(msg)) return;
    try {
      await deleteUploads.mutateAsync(ids);
      setSelected(new Set());
    } catch (e) {
      window.alert(
        `Delete failed: ${e instanceof Error ? e.message : String(e)}`,
      );
    }
  };

  if (!projectId) {
    return (
      <section className={styles.section}>
        <div className={styles.err}>
          Missing projectId in URL. Go back to{" "}
          <Link to="/projects">Projects</Link>.
        </div>
      </section>
    );
  }

  const onDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  };
  const onDragLeave = () => setDragOver(false);
  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    onPickFiles(e.dataTransfer.files);
  };

  const onSubmitAddFiles = async () => {
    // Nothing to do if neither new files nor reused (re-picked unchanged)
    // already-attached files are staged.
    if (pickedFiles.length === 0 && reusedAttachedUploads.length === 0) return;
    try {
      await uploader.submit({
        project_id: projectId,
        project_name: fileProject
          ? projectDisplayName(fileProject)
          : (projectId ?? ""),
        files: pickedFiles,
        // Re-picked already-attached unchanged files: reference, don't re-upload.
        reuse_uploads: reuseUploadsForSubmit(),
      });
      // Fire any deferred deletes the operator confirmed when replacing a
      // same-name attached file via the duplicate modal.
      if (pendingDeletes.length > 0) {
        deleteUploads.mutate(pendingDeletes);
        clearPendingDeletes();
      }
      // pickedFiles is cleared by the useEffect above on stage "done".
    } catch (e) {
      // The submit's error surfaces inline below; no window.alert noise here.
      void e;
    }
  };

  const composition = summariseTree(uploadsTreeQuery.data);
  const totalBytes = uploads.reduce((sum, u) => sum + (u.size_bytes ?? 0), 0);
  // Feed every per-row extraction view from ONE batched poll instead of one
  // /uploads/extracted request per row (the Files-page N+1 that storms the RDS
  // connection pool). The provider makes descendant useUploadExtracted calls
  // read from this single batch.
  const submitting =
    thisProjectSubmit?.stage === "uploading";
  const stagedCount = pickedFiles.length + reusedAttachedUploads.length;
  const submitLabel = submitting
    ? (thisProjectSubmit?.label ?? "Submitting…")
    : stagedCount === 0
      ? "Pick files to add"
      : `Add ${stagedCount} file${stagedCount === 1 ? "" : "s"}`;

  // The direct list and the unaccounted list share one table, actions and all.
  const renderFilesTable = (rows: UploadRecord[]) => (
    <table className={styles.table}>
      <thead>
        <tr>
          <th className={styles.th} style={{ width: 36 }}>
            {(() => {
              // Production-site rows are excluded from select-all
              // - they aren't bulk-actionable (re-created on the
              // next refresh-site call).
              const selectableIds = rows
                .filter((u) => u.source !== "production_site")
                .map((u) => u.upload_id);
              // Per table: the selection spans both lists on the page.
              const picked = selectableIds.filter((id) => selected.has(id)).length;
              const all = picked === selectableIds.length && picked > 0;
              return (
                <input
                  type="checkbox"
                  aria-label="Select all"
                  checked={all}
                  ref={(el) => {
                    if (el) {
                      el.indeterminate = picked > 0 && !all;
                    }
                  }}
                  onChange={() =>
                    setSelected((prev) => {
                      const next = new Set(prev);
                      selectableIds.forEach((id) =>
                        all ? next.delete(id) : next.add(id),
                      );
                      return next;
                    })
                  }
                />
              );
            })()}
          </th>
          <th className={styles.th}>Name</th>
          <th className={styles.th}>Source</th>
          <th className={styles.th}>Type</th>
          <th className={styles.th}>Size</th>
          <th className={styles.th}>Uploaded</th>
          <th className={styles.th} style={{ width: 360 }}>
            Actions
          </th>
        </tr>
      </thead>
      <tbody>
        {rows.flatMap((u) => [
          <tr key={`${u.upload_id}-row`} className={styles.row}>
            <td className={styles.td}>
              {/* Production-site rows aren't selectable - bulk
                  delete / refresh don't apply to them, since
                  they're sourced from BMA on every refresh. */}
              {u.source !== "production_site" && (
                <input
                  type="checkbox"
                  checked={selected.has(u.upload_id)}
                  onChange={() => toggle(u.upload_id)}
                  aria-label={`Select ${u.original_name}`}
                />
              )}
            </td>
            <td className={styles.td}>
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  flexWrap: "wrap",
                  rowGap: 4,
                }}
              >

                <ExtractionIssueMarker uploadId={u.upload_id} />
                {(u.marked_out_of_date || u.out_of_date_fields.length > 0) && (
                  <OutOfDateIcon
                    position="left"
                    tooltip="Marked out of date"
                  />
                )}
                <span className={styles.fileName}>{u.original_name}</span>
                {u.deprecated && <DeprecatedBadge />}
                {u.references_multiple_sites && (
                  <SiteWarningBadge
                    label="Multiple sites"
                    title={
                      u.multi_site_note
                        ? `This file covers more than one site: ${u.multi_site_note}. Check any value from it belongs to THIS site.`
                        : "This file covers more than one site. Check any value from it belongs to THIS site."
                    }
                  />
                )}
                <PossibleOtherSiteBadge uploadId={u.upload_id} />
                <RegisterBadge documentId={u.upload_id} projectId={projectId} />
              </span>
              {/* Procurement-comparison PDFs have no extractor (they are
                  an output artefact, not an input), so the extracted-content
                  toggle would have nothing to expand. Hide it entirely. */}
              {/* A large scan being transcribed across OCR segments shows
                  a live determinate bar instead of the "Show data (…)"
                  toggle (there's nothing to show until the transcription
                  lands). The bar advances via the list poll's
                  running-state interval refetch. */}
              {u.extraction_status === "running" &&
              u.extraction_progress ? (
                <ExtractionProgressBar progress={u.extraction_progress} />
              ) : (
                u.kind !== "procurement_comparison" && (
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 8,
                      flexWrap: "wrap",
                    }}
                  >
                    <ExtractionStatusButton
                      uploadId={u.upload_id}
                      isExpanded={expanded.has(u.upload_id)}
                      onToggle={() => toggleExpanded(u.upload_id)}
                      chars={u.extracted_text_chars}
                      pages={u.total_pages}
                      heldUntil={u.extraction_not_before}
                    />
                    {u.kind === "email_thread" && (
                      <EmailThreadAttachmentsToggle
                        uploadId={u.upload_id}
                        expanded={attachmentsExpanded.has(u.upload_id)}
                        onToggle={() => toggleAttachmentsExpanded(u.upload_id)}
                      />
                    )}
                  </span>
                )
              )}
            </td>
            <td className={styles.tdProvenance}>
              {/* Real provenance, not a bare source badge (INRT-2560): every
                  row answers "where did this file come from?" via the ONE
                  shared SightingList. The badge it replaced said only which
                  channel it arrived on and repeated what the sighting line
                  already says.

                  ``collapseSingle`` keeps the column uniform - EVERY row with
                  provenance shows a counter ("Seen in 1 place" / "Seen in 3
                  places"), rather than short rows rendering a full sentence
                  inline while longer ones collapse. Rows with no sighting at
                  all (BMA-synced production_site rows, calculated /
                  agent-generated artefacts) fall back to a one-line label
                  derived from the canonical origin. */}
              {u.sightings && u.sightings.length > 0 ? (
                <SightingList sightings={u.sightings} collapseSingle />
              ) : (
                <span className={styles.provenanceFallback}>
                  {provenanceFallback(u)}
                </span>
              )}
              {/* The two details that live on the UPLOAD rather than on a
                  sighting, so SightingList can't render them: which mailbox
                  a hand-imported Gmail thread came from, and when a
                  Drive/Gmail row was last re-fetched from its source. Both
                  were in the old Source badge's tooltip; keep them rather
                  than losing the detail with the badge. */}
              {provenanceDetail(u) && (
                <span className={styles.provenanceDetail}>
                  {provenanceDetail(u)}
                </span>
              )}
            </td>
            <td
              className={styles.tdSmall}
              title={u.content_type}
            >
              {/* The kind is read-only; each row offers the re-extract that
                  fits its source. procurement_comparison is a calculated
                  output with nothing to re-extract, and an email thread's
                  cell doubles as its "3 messages" summary. */}
              {u.kind === "email_thread" ? (
                <span
                  style={{
                    display: "inline-flex",
                    gap: 8,
                    alignItems: "baseline",
                    flexWrap: "wrap",
                  }}
                >
                  <EmailThreadTypeCell
                    uploadId={u.upload_id}
                    messageCount={threadMessageCount(u.sightings)}
                  />
                  <ReextractLink
                    projectId={projectId ?? ""}
                    uploadId={u.upload_id}
                    extractionStatus={u.extraction_status}
                  />
                </span>
              ) : u.source === "production_site" ? (
                // A BMA-synced row (the survey or the cost sheet) is
                // re-pulled by Refresh site, so it gets just the kind label.
                <span>{kindLabel(u.kind)}</span>
              ) : u.kind === "procurement_comparison" ? (
                <span>{kindLabel(u.kind)}</span>
              ) : (
                <span
                  style={{
                    display: "inline-flex",
                    gap: 6,
                    alignItems: "baseline",
                    fontSize: 12,
                  }}
                >
                  {kindLabel(u.kind) && <span>{kindLabel(u.kind)}</span>}
                  <ReextractLink
                    projectId={projectId ?? ""}
                    uploadId={u.upload_id}
                    extractionStatus={u.extraction_status}
                  />
                </span>
              )}
            </td>
            <td className={styles.tdSmall}>
              {u.size_bytes ? formatBytes(u.size_bytes) : "-"}
            </td>
            <td className={styles.tdSmall}>
              {formatTimestamp(u.completed_at)}
            </td>
            <td className={styles.td}>
              {u.source === "production_site" ? (
                // No "Open" affordance - the survey is synced from BMA,
                // there's no openable file. Re-extract now lives as a
                // textual link next to the Kind (above), aligned with
                // every other file type's Re-extract affordance.
                null
              ) : u.credentials_redacted ? (
                <span
                  className={styles.textOnlyNote}
                  title="This file holds login details, so it cannot be opened. Its text is readable with each credential removed."
                >
                  Credentials removed - text only
                </span>
              ) : u.drive_file_id ? (
                // Drive-sourced (manual picker): open the Drive
                // file directly in a new tab. Uses the same
                // ``<button>`` element + styling as the regular
                // Open so the layout stays identical to before
                // this branch.
                <button
                  type="button"
                  className={styles.openBtn}
                  onClick={() =>
                    window.open(
                      `https://drive.google.com/file/d/${encodeURIComponent(u.drive_file_id ?? "")}/view`,
                      "_blank",
                      "noopener,noreferrer",
                    )
                  }
                  disabled={demo}
                  title={
                    demo
                      ? "Disabled in demo mode"
                      : "Open this file in Drive in a new tab"
                  }
                >
                  Open in Drive
                </button>
              ) : (
                <button
                  type="button"
                  className={styles.openBtn}
                  onClick={() => void onOpen(u.upload_id)}
                  disabled={busyOpen === u.upload_id || demo}
                  title={
                    demo
                      ? "Disabled in demo mode"
                      : "Open this file in a new tab"
                  }
                >
                  {busyOpen === u.upload_id ? "Opening…" : "Open"}
                </button>
              )}
              {((u.source === "drive" && u.drive_file_id) ||
                (u.source === "gmail" && u.gmail_thread_id)) &&
                (() => {
                  // Drive rows: always enabled. Gmail rows:
                  // enabled only for the operator who imported
                  // the thread (pulled_by); otherwise rendered
                  // disabled with a who-can-refresh title.
                  const gmailLocked =
                    u.source === "gmail" && u.pulled_by !== user?.id;
                  return (
                    <button
                      type="button"
                      className={styles.openBtn}
                      onClick={() => void onRefreshRows([u])}
                      disabled={busyRefresh.has(u.upload_id) || gmailLocked}
                      title={
                        gmailLocked
                          ? `Refreshable only by ${u.pulled_by_name ?? "the importer"}`
                          : u.source === "gmail"
                            ? "Re-pull the latest thread + attachments from Gmail"
                            : "Re-fetch the latest bytes from Google Drive"
                      }
                    >
                      {busyRefresh.has(u.upload_id)
                        ? "Refreshing…"
                        : "Refresh"}
                    </button>
                  );
                })()}
              {(() => {
                const isOutOfDate =
                  u.marked_out_of_date || u.out_of_date_fields.length > 0;
                return (
                  <button
                    type="button"
                    className={styles.openBtn}
                    onClick={() =>
                      void onToggleFreshness(u.upload_id, !isOutOfDate)
                    }
                    disabled={busyFreshness === u.upload_id}
                    title={
                      isOutOfDate
                        ? "Mark this file as fresh / current again"
                        : "Mark this file as out of date"
                    }
                  >
                    {busyFreshness === u.upload_id
                      ? "Saving…"
                      : isOutOfDate
                        ? "Mark fresh"
                        : "Mark out of date"}
                  </button>
                );
              })()}
              {(u.source ?? "").startsWith("gmail_sync") && (
                <button
                  type="button"
                  className={styles.openBtn}
                  onClick={() =>
                    void onExcludeWrongSiteOne(u.upload_id, u.original_name)
                  }
                  disabled={busyQuarantine === u.upload_id || demo}
                  title={
                    demo
                      ? "Disabled in demo mode"
                      : "Exclude this file as belonging to a different site (reversible from Settings)"
                  }
                >
                  {busyQuarantine === u.upload_id ? "Excluding…" : "Wrong site"}
                </button>
              )}
              {u.source !== "production_site" && (
                <button
                  type="button"
                  className={styles.deleteBtn}
                  onClick={() => void onDeleteOne(u.upload_id, u.original_name)}
                  disabled={
                    (deleteUploads.isLoading && busyDelete === u.upload_id) ||
                    demo
                  }
                  title={demo ? "Disabled in demo mode" : "Delete this file"}
                >
                  {deleteUploads.isLoading && busyDelete === u.upload_id
                    ? "Deleting…"
                    : "Delete"}
                </button>
              )}
            </td>
          </tr>,
          u.kind === "email_thread" &&
          attachmentsExpanded.has(u.upload_id) ? (
            <EmailThreadAttachmentsRows
              key={`${u.upload_id}-attachments`}
              uploadId={u.upload_id}
              projectId={projectId}
              colSpan={7}
            />
          ) : null,
          expanded.has(u.upload_id) ? (
            <tr key={`${u.upload_id}-extracted`}>
              <td colSpan={7} style={{ padding: 0, background: "#fafafa" }}>
                <DemoBlur>
                  <ExtractedContentPanel
                    uploadId={u.upload_id}
                    chars={u.extracted_text_chars}
                    pages={u.total_pages}
                  />
                </DemoBlur>
              </td>
            </tr>
          ) : null,
        ])}
      </tbody>
    </table>
  );

  return (
    <UploadsExtractedProvider uploadIds={uploadIds}>
    <RegistersForDocumentsProvider
      documentIds={(uploadsQuery.data ?? []).map((u) => u.upload_id)}
    >
    {/* Fed from the RAW list (not the filtered ``uploads``): the Gmail tree's
        nested attachment rows are excluded from this tab's own table but still
        need their provenance, and they're in the list query already. */}
    <SightingsByUploadProvider uploads={uploadsQuery.data ?? []}>
    <section className={styles.section}>
      {/* Whole-project extraction state, above every list: fed from the RAW
          uploads (not the ``uploads`` the flat list renders) so it covers the
          Drive subscription, the Gmail listener and hand-attached files alike. */}
      <ExtractionSummaryBar uploads={uploadsQuery.data} />
      {projectId && <FailedExtractionsPanel projectId={projectId} />}
      {projectId && (
        <>
          <ExtractedClausesSection projectId={projectId} />
          <section
            style={{
              marginBottom: 32,
              paddingBottom: 24,
              borderBottom: "2px solid #e5e7eb",
            }}
          >
            <h2
              style={{ marginTop: 0, marginBottom: 4, fontSize: 18, color: "#1f2937" }}
            >
              Files from Drive subscription
              {/* Nothing until the payload is in: "0 files" over a populated
                  tree is what an unguarded count says while /uploads/tree is
                  still loading, or has failed. */}
              {uploadsTreeQuery.data && (
                <SectionCount>{plural(composition.driveFiles, "file")}</SectionCount>
              )}
            </h2>
            <p
              style={{
                margin: "0 0 16px",
                fontSize: 13,
                color: "#6b7280",
              }}
            >
              Auto-pulled from the subscribed Drive folders and tracked files on
              the 15-min cron, grouped by their original folder structure. Configure the
              subscription from the project Settings tab.
            </p>
            <DriveSyncedFilesTree projectId={projectId} />
          </section>
          <section
            style={{
              marginBottom: 32,
              paddingBottom: 24,
              borderBottom: "2px solid #e5e7eb",
            }}
          >
            <h2
              style={{ marginTop: 0, marginBottom: 4, fontSize: 18, color: "#1f2937" }}
            >
              Threads from email
              {uploadsTreeQuery.data && (
                <SectionCount>
                  {[
                    plural(composition.threads, "thread"),
                    composition.messages !== null &&
                      // A floor, not a total, when some threads never recorded
                      // a count - so it is not printed as one.
                      (composition.messagesComplete
                        ? plural(composition.messages, "message")
                        : `${plural(composition.messages, "message")}+`),
                    plural(composition.threadAttachments, "attachment"),
                  ]
                    .filter((part): part is string => typeof part === "string")
                    .join(" · ")}
                </SectionCount>
              )}
            </h2>
            <p
              style={{
                margin: "0 0 16px",
                fontSize: 13,
                color: "#6b7280",
              }}
            >
              Filed by the daily sweep of the inboxes this project draws from.
              Re-pulled when the thread gets new messages.
            </p>
            <GmailSyncedFilesTree projectId={projectId} />
          </section>
        </>
      )}
      <section aria-labelledby="files-direct-heading">
      <h2
        id="files-direct-heading"
        style={{ marginTop: 0, marginBottom: 4, fontSize: 18, color: "#1f2937" }}
      >
        Files attached directly
        {uploadsQuery.data && (
          <SectionCount>
            {/* Live rows only, like the trees above: tombstones render here struck
                through and are not files the project has. */}
            {plural(uploads.filter((u) => u.status === "active").length, "file")}
          </SectionCount>
        )}
      </h2>
      <p style={{ margin: "0 0 16px", fontSize: 13, color: "#6b7280" }}>
        Files uploaded manually via local drop, the Drive picker, or Gmail import.
      </p>
      <section
        className={`${styles.uploadCard} ${
          uploadSource === "local" && dragOver ? styles.uploadCardActive : ""
        }`}
        onDragOver={uploadSource === "local" ? onDragOver : undefined}
        onDragLeave={uploadSource === "local" ? onDragLeave : undefined}
        onDrop={uploadSource === "local" ? onDrop : undefined}
      >
        <h3 className={styles.uploadTitle}>Add files</h3>
        <p className={styles.uploadHelp}>
          Drop local files, pick from Google Drive, or import Gmail threads.
          Files from any source land in the same queue below; click "Add files"
          to upload them. Each is read in the background.
        </p>

        <div
          className={styles.sourceTabs}
          role="tablist"
          aria-label="Upload source"
        >
          {(
            [
              {
                id: "local" as const,
                label: "Local files",
                hint: "Drop or browse",
              },
              {
                id: "drive" as const,
                label: "Google Drive",
                hint: "Pick a folder or files",
              },
              {
                id: "gmail" as const,
                label: "Gmail",
                hint: "Import email threads",
              },
            ]
          ).map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={uploadSource === tab.id}
              className={`${styles.sourceTab} ${
                uploadSource === tab.id ? styles.sourceTabActive : ""
              }`}
              onClick={() => setUploadSource(tab.id)}
              disabled={submitting}
            >
              <span className={styles.sourceTabLabel}>{tab.label}</span>
              <span className={styles.sourceTabHint}>{tab.hint}</span>
            </button>
          ))}
        </div>

        {uploadSource === "local" && (
          <div className={styles.uploadActionsRow}>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              style={{ display: "none" }}
              onChange={(e) => {
                onPickFiles(e.target.files);
                if (fileInputRef.current) fileInputRef.current.value = "";
              }}
            />
            <button
              type="button"
              className={styles.browseBtn}
              onClick={() => fileInputRef.current?.click()}
              disabled={submitting}
            >
              Browse...
            </button>
            {pickedFiles.length > 0 && (
              <button
                type="button"
                className={styles.clearBtn}
                onClick={resetFileIntake}
                disabled={submitting}
              >
                Clear queue
              </button>
            )}
          </div>
        )}

        {uploadSource === "drive" && (
          <DrivePickerPanel
            onAddFiles={addFiles}
            disabled={submitting}
            embedded
            {...drivePickerReuse}
          />
        )}

        {uploadSource === "gmail" && (
          <GmailSearchPanel projectId={projectId} disabled={submitting} embedded />
        )}

        {pickedFiles.length > 0 && (
          <ul className={styles.pickedList}>
            {pickedFiles.map((f, i) => (
              <li key={`${f.name}-${i}`} className={styles.pickedRow}>
                <span className={styles.pickedName}>{f.name}</span>
                <span className={styles.pickedSize}>
                  {formatBytes(f.size)}
                </span>
                <button
                  type="button"
                  className={styles.pickedRemove}
                  onClick={() => removeFile(i)}
                  disabled={submitting}
                  aria-label={`Remove ${f.name}`}
                  title="Remove from queue"
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
        )}

        {reusedAttachedUploads.length > 0 && (
          <div className={styles.askHint}>
            {reusedAttachedUploads.length} re-picked file
            {reusedAttachedUploads.length === 1 ? " is" : "s are"} already
            attached and will be reused (not re-uploaded).
          </div>
        )}

        <DuplicateResolverModal {...dupResolverProps} />

        <div className={styles.submitRow}>
          <button
            type="button"
            className={styles.submitBtn}
            onClick={onSubmitAddFiles}
            disabled={
              (pickedFiles.length === 0 &&
                reusedAttachedUploads.length === 0) ||
              submitting
            }
          >
            {submitLabel}
          </button>
          {thisProjectSubmit?.stage === "done" && (
            <span className={styles.submitOk}>
              Done. The new files are in the list below.
            </span>
          )}
          {thisProjectSubmit?.stage === "error" && thisProjectSubmit?.error && (
            <span className={styles.submitErr}>
              Couldn't upload the files: {thisProjectSubmit.error.message}
            </span>
          )}
        </div>
      </section>

      <div className={styles.head}>
        <p className={styles.lede}>
          Files already attached to this project. Open, download, or delete
          them below.
        </p>
      </div>

      {uploadsQuery.isLoading && (
        <div className={styles.empty}>Loading files…</div>
      )}
      {uploadsQuery.isError && (
        <div className={styles.err}>
          Couldn't load the file list:{" "}
          {(uploadsQuery.error as Error)?.message ?? "unknown error"}
        </div>
      )}

      {!uploadsQuery.isLoading &&
        !uploadsQuery.isError &&
        uploads.length === 0 &&
        unaccounted.length === 0 && (
        <div className={styles.empty}>
          <strong>No files attached to this project yet.</strong>
          <p>Use the Add files box above to upload the first batch.</p>
        </div>
      )}

      {uploads.length + unaccounted.length > 0 && (
        <>
          <div className={styles.summaryRow}>
            <div className={styles.summary}>
              {uploads.length > 0 &&
                `${uploads.length} file${uploads.length === 1 ? "" : "s"} · ${formatBytes(totalBytes)} total`}
            </div>
            <div className={styles.bulkBar}>
              {selected.size > 0 && (
                <>
                  <span className={styles.selectedCount}>
                    {selected.size} selected
                  </span>
                  <button
                    type="button"
                    className={styles.clearBtn}
                    onClick={() => setSelected(new Set())}
                  >
                    Clear
                  </button>
                  {(() => {
                    const selectedRows = [...uploads, ...unaccounted].filter((u) =>
                      selected.has(u.upload_id),
                    );
                    const refreshableCount = selectedRows.filter(
                      (r) =>
                        (r.source === "drive" &&
                          typeof r.drive_file_id === "string") ||
                        (r.source === "gmail" &&
                          typeof r.gmail_thread_id === "string" &&
                          r.pulled_by === user?.id),
                    ).length;
                    return refreshableCount > 0 ? (
                      <button
                        type="button"
                        className={styles.openBtn}
                        onClick={() => void onRefreshRows(selectedRows)}
                        disabled={refreshUploads.isLoading}
                        title="Re-fetch the latest bytes from the source (Google Drive / Gmail) for every selected refreshable file"
                      >
                        {refreshUploads.isLoading
                          ? `Refreshing ${refreshableCount}…`
                          : `Refresh ${refreshableCount} file${refreshableCount === 1 ? "" : "s"}`}
                      </button>
                    ) : null;
                  })()}
                  <button
                    type="button"
                    className={styles.bulkDeleteBtn}
                    onClick={onBulkDelete}
                    disabled={deleteUploads.isLoading || demo}
                    title={demo ? "Disabled in demo mode" : undefined}
                  >
                    {deleteUploads.isLoading
                      ? `Deleting ${selected.size}…`
                      : `Delete ${selected.size} file${selected.size === 1 ? "" : "s"}`}
                  </button>
                </>
              )}
            </div>
          </div>

          {uploads.length > 0 && renderFilesTable(uploads)}
        </>
      )}
      </section>
      {unaccounted.length > 0 && (
        <section aria-labelledby="files-unaccounted-heading" style={{ marginTop: 32 }}>
          <h2
            id="files-unaccounted-heading"
            style={{ marginTop: 0, marginBottom: 4, fontSize: 18, color: "#1f2937" }}
          >
            Unaccounted for
            <SectionCount>{plural(unaccounted.length, "file")}</SectionCount>
          </h2>
          <p
            style={{
              margin: "0 0 16px",
              fontSize: 13,
              color: "#92400e",
              background: "#fffbeb",
              border: "1px solid #fde68a",
              borderRadius: 6,
              padding: "8px 12px",
            }}
          >
            These files are on the project, but no Drive folder, email or hand upload
            carries them any more, so they belong in none of the sections above. That is
            a platform bug: please report it with the project name. The Source column
            shows where each one was last seen.
          </p>
          {renderFilesTable(unaccounted)}
        </section>
      )}
      {deletedArrivals.length > 0 && (
        <DeletedArrivalsSection arrivals={deletedArrivals} />
      )}
    </section>
    </SightingsByUploadProvider>
    </RegistersForDocumentsProvider>
    </UploadsExtractedProvider>
  );
};

// Operator-deleted files that keep arriving via an automated path (design 3.3
// step 3): the delete is honoured (bytes gone, row tombstoned) but the re-ingest
// is SUPPRESSED - shown here so the suppression is visible, never silent.
const DeletedArrivalsSection = ({
  arrivals,
}: {
  arrivals: DeletedArrival[];
}) => {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ marginTop: 16, fontSize: 13 }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        style={{
          background: "none",
          border: "none",
          color: "#6b7280",
          cursor: "pointer",
          padding: 0,
        }}
      >
        <Chevron tree open={open} /> Deleted, but the same content keeps arriving (
        {arrivals.length})
      </button>
      {open && (
        <>
          <p style={{ margin: "6px 0 0", color: "#6b7280", fontSize: 12 }}>
            You deleted these, and an automated source keeps re-sending the same
            content. Rather than quietly re-adding them, the platform records
            that they arrived and leaves them deleted - so the deletion holds
            and the fact that something upstream still has the file is not
            hidden from you.
          </p>
          <ul style={{ margin: "6px 0 0 16px", color: "#6b7280" }}>
          {arrivals.map((a) => (
            <li key={a.upload_id}>
              <strong>{a.original_name}</strong> - still arriving from{" "}
              {Array.from(new Set(a.sightings.map((s) => s.source))).join(", ")}.
              Re-upload it by hand if you want it back.
            </li>
          ))}
          </ul>
        </>
      )}
    </div>
  );
};
